import React, { useState, useRef, useLayoutEffect } from "react";
import { Box } from "@mui/material";

const PAGE_WIDTH = "210mm";
const PAGE_HEIGHT = "297mm";
const PAGE_PADDING = "0mm";
const PAGE_HEIGHT_PRINT = "calc(297mm - 2mm)";

const modes = {
  portrait: {
    PAGE_WIDTH: "210mm",
    PAGE_HEIGHT: "297mm",
    PAGE_HEIGHT_PRINT: "296.5mm", // slightly under 297mm to avoid blank trailing page
  },
  landscape: {
    PAGE_WIDTH: "297mm",
    PAGE_HEIGHT: "210mm",
    PAGE_HEIGHT_PRINT: "209.5mm",
  },
};

export default function Page({ children, footer, mode = "portrait" }) {
  const footerRef = useRef(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const { PAGE_WIDTH, PAGE_HEIGHT, PAGE_HEIGHT_PRINT } = modes[mode];

  useLayoutEffect(() => {
    if (!footerRef.current) return;

    // Measures the exact rendered height of the footer block in pixels
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setFooterHeight(
          entry.borderBoxSize[0]?.blockSize || entry.contentRect.height,
        );
      }
    });

    observer.observe(footerRef.current);
    return () => observer.disconnect();
  }, [footer]);

  // Pass max content height via render prop or standard node context
  const maxContentHeight = footerHeight
    ? `calc(${PAGE_HEIGHT} - ${footerHeight}px)`
    : PAGE_HEIGHT;

  return (
    <>
      <style>{`
       @page portrait {
    size: A4 portrait;
    margin: 0;
  }
  @page landscape {
    size: A4 landscape;
    margin: 0;
  }
  @media print {
    body { margin: 0 !important; }
  }
      `}</style>

      <Box
        sx={{
          width: PAGE_WIDTH,
          minHeight: PAGE_HEIGHT,
          boxSizing: "border-box",
          paddingTop: "4px",
          marginInline: "auto",
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 0 6px rgba(0,0,0,0.15)",
          "@media print": {
            boxShadow: "none",
            margin: 0,
            page:mode,
            minHeight: "auto",
            height: PAGE_HEIGHT_PRINT,
            breakBefore: "page",
            pageBreakBefore: "always",
            breakInside: "auto",
          },
          "&:first-of-type": {
            "@media print": {
              breakBefore: "auto",
              pageBreakBefore: "auto",
            },
          },
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            // Calculate height minus footer dynamically
            maxHeight: maxContentHeight,
            overflow: "hidden", // Prevents body overflow past calculated printable bounds
          }}
        >
          {typeof children === "function"
            ? children({ maxContentHeight, footerHeight })
            : children}
        </Box>

        {footer && (
          <Box
            ref={footerRef}
            sx={{
              mt: "auto",
              pt: 2,
              borderTop: "1px solid #ddd",
              breakInside: "avoid",
            }}
          >
            {footer}
          </Box>
        )}
      </Box>
    </>
  );
}
